import { useId, useState, type FormEvent } from "react";
import type { AppointmentView, LeadCardResponse } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../design/components/Button";
import { Reason } from "../design/components/Reason";
import { StatusPill } from "../design/components/StatusPill";
import { TextField } from "../design/components/TextField";
import { useToast } from "../design/components/Toast";
import { messageForError } from "../i18n/errors";
import { formatNumericDateTime } from "../i18n/format";
import { useT } from "../i18n/locale";
import { useLeadView } from "../leads/lead-queries";
import { leadOpportunityQuery } from "../opportunities/opportunity-queries";
import { opportunityScopeOf, useAccess } from "../session/access";
import type { TabContext } from "../session/tab-context";
import { stageText } from "./appointment-texts";
import { appointmentsQuery, useChangeAppointment, type AppointmentCommand } from "./appointments-queries";
import { BookAppointmentDialog } from "./BookAppointmentDialog";
import "./appointments.css";

// Disco e demo nel Riepilogo della scheda (piano M6, R7, Task 6; SEZ-9, TASK-11): la fase dell'opportunità aperta (quando il riquadro
// «Opportunità» di R8, Task 25, non la mostra già) e gli appuntamenti del Lead, quelli aperti prima. Di ognuno: quando, quanto dura,
// dove sta (Google Calendar con il link Meet vero, in aggiornamento, non aggiornato, soltanto in Gioia365) e, se è aperto, «Sposta»
// e «Annulla» con il motivo; dall'ora
// dell'appuntamento anche «Fatta» (con i minuti facoltativi) e «Il Lead non si è presentato». Ogni comando porta la versione letta:
// una modifica arrivata nel frattempo torna con il suo motivo e la sezione si rilegge.

type Props = { tab: TabContext; card: LeadCardResponse; timeZone: string };

export function AppointmentsSection({ tab, card, timeZone }: Props) {
  const t = useT();
  const view = useLeadView();
  const titleId = useId();
  const appointments = useQuery(appointmentsQuery(tab, card.lead.id, view));
  // Il riquadro «Opportunità» (R8, Task 25), con la sua stessa lettura (la stessa chiave: nessuna richiesta in più). La fase qui si
  // tace soltanto se il riquadro mostra proprio questa Opportunità aperta: chi non vede le Opportunità di un collega la legge qui.
  const boxShown = opportunityScopeOf(useAccess(), "view") !== "none";
  const boxed = useQuery({ ...leadOpportunityQuery(tab, card.lead.id, view), enabled: boxShown });
  const data = appointments.data;
  const stageInBox = boxShown && data?.opportunity !== null && data?.opportunity !== undefined && boxed.data?.id === data.opportunity.id;
  // Senza appuntamenti da mostrare (anche quando sono tutti di colleghi, fuori dai Task che si vedono) e senza la fase da dire, niente.
  if (!data || (data.appointments.length === 0 && (data.opportunity === null || stageInBox))) return null;
  const sorted = [...data.appointments].sort((left, right) => Number(right.status === "open") - Number(left.status === "open"));
  return (
    <section className="g-lead-summary__section g-appointments" aria-labelledby={titleId}>
      <h3 id={titleId}>{t("appointments.block.title")}</h3>
      {/* La fase la dice il riquadro «Opportunità» quando mostra questa stessa Opportunità (R8, Task 25). */}
      {data.opportunity && !stageInBox ? (
        <p className="g-appointments__stage">
          {t("appointments.block.opportunity", { stage: stageText(t, data.opportunity.stage) })}
          {data.opportunity.nextStep === "reschedule" ? <span className="g-appointments__reschedule"> · {t("appointments.block.reschedule")}</span> : null}
        </p>
      ) : null}
      <ul className="g-appointments__list">
        {sorted.map((appointment) => (
          <AppointmentItem key={appointment.id} tab={tab} card={card} appointment={appointment} timeZone={timeZone} readAt={appointments.dataUpdatedAt} />
        ))}
      </ul>
    </section>
  );
}

/**
 * Trascrizione, note di Gemini e registrazione del Meet (SEZ-18): i link aprono Google in una scheda nuova, con i permessi di Google.
 * Prima della fine non si dice niente; dopo, se non ci sono, una riga dice perché (in attesa, non accese, Google scollegato, senza
 * Google Calendar).
 */
function MeetFiles({ appointment, kind, ended }: { appointment: AppointmentView; kind: string; ended: boolean }) {
  const t = useT();
  const status = appointment.meetFilesStatus;
  if (status === "found" && appointment.meetFiles.length > 0) {
    return (
      <ul className="g-appointments__files">
        {appointment.meetFiles.map((file) => {
          const name = t(`appointments.block.files.${file.kind}`);
          return (
            <li key={file.url}>
              <a href={file.url} target="_blank" rel="noopener noreferrer" aria-label={t("appointments.block.files.opensGoogle", { name })} title={file.title || undefined}>
                {name}
              </a>
            </li>
          );
        })}
      </ul>
    );
  }
  if (!ended || status === "not_applicable" || status === "found") return null;
  return <Reason>{t(`appointments.block.files.${status}`, { kind: kind.toLowerCase() })}</Reason>;
}

type ItemProps = {
  tab: TabContext;
  card: LeadCardResponse;
  appointment: AppointmentView;
  timeZone: string;
  /** Quando gli appuntamenti sono stati letti (millisecondi): gli esiti si offrono da lì; la lettura si ripete ogni minuto. */
  readAt: number;
};

function AppointmentItem({ tab, card, appointment, timeZone, readAt }: ItemProps) {
  const t = useT();
  const toast = useToast();
  const view = useLeadView();
  const ids = useId();
  const change = useChangeAppointment(tab, card.lead.id, view);
  const [mode, setMode] = useState<"idle" | "held" | "cancel" | "move">("idle");
  const [heldMinutes, setHeldMinutes] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const kind = t(`appointments.kind.${appointment.kind}`);
  const open = appointment.status === "open";
  const started = readAt >= Date.parse(appointment.startsAt);
  const canChange = open && card.permissions.canUpdate;

  async function send(command: AppointmentCommand, done: string) {
    setError(null);
    try {
      await change.mutateAsync({ taskId: appointment.id, command });
      toast.show(done, "success");
      setMode("idle");
    } catch (failure) {
      setError(messageForError(t, failure));
    }
  }
  function submitHeld(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const minutes = heldMinutes.trim() === "" ? undefined : Number(heldMinutes);
    if (minutes !== undefined && (!Number.isInteger(minutes) || minutes < 0 || minutes > 480)) {
      setError(t("appointments.dialog.minutesHint"));
      return;
    }
    void send({ action: "result", result: "held", version: appointment.version, ...(minutes === undefined ? {} : { heldMinutes: minutes }) }, t("appointments.block.saved"));
  }
  function submitCancel(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (reason.trim() === "") return;
    void send({ action: "cancel", reason: reason.trim(), version: appointment.version }, t("appointments.block.canceled"));
  }

  const status = appointment.status === "done" && appointment.result ? t(`appointments.block.result.${appointment.result}`) : t(`appointments.block.status.${appointment.status}`);
  return (
    <li className="g-appointments__item" data-status={appointment.status}>
      <p className="g-appointments__what">
        <strong>{t("appointments.block.item", { kind, when: formatNumericDateTime(timeZone, appointment.startsAt), minutes: appointment.minutes })}</strong>
      </p>
      <p className="g-appointments__facts">
        <StatusPill
          tone={appointment.status === "canceled" ? "neutral" : appointment.status === "done" ? "success" : "warning"}
          icon={appointment.status === "canceled" ? "minus" : appointment.status === "done" ? "check" : "clock"}
        >
          {status}
        </StatusPill>
        {open ? <span className="g-appointments__calendar">{t(`appointments.block.calendar.${appointment.calendarState}`)}</span> : null}
      </p>
      {open && appointment.meetUrl ? (
        <p>
          <a href={appointment.meetUrl} target="_blank" rel="noopener noreferrer">
            {t("appointments.block.meet")}
          </a>
        </p>
      ) : null}
      <MeetFiles appointment={appointment} kind={kind} ended={readAt >= Date.parse(appointment.startsAt) + appointment.minutes * 60_000} />
      {error ? (
        <Reason tone="warning" role="alert">
          {error}
        </Reason>
      ) : null}
      {canChange && mode === "idle" ? (
        <div className="g-appointments__actions">
          {started ? (
            <>
              <Button disabled={change.isPending} onClick={() => setMode("held")}>
                {t("appointments.block.held")}
              </Button>
              <Button disabled={change.isPending} onClick={() => void send({ action: "result", result: "no_show", version: appointment.version }, t("appointments.block.saved"))}>
                {t("appointments.block.noShow")}
              </Button>
            </>
          ) : null}
          <Button disabled={change.isPending} aria-haspopup="dialog" onClick={() => setMode("move")}>
            {t("appointments.block.move")}
          </Button>
          <Button disabled={change.isPending} onClick={() => setMode("cancel")}>
            {t("appointments.block.cancel")}
          </Button>
          {!started ? <Reason>{t("appointments.block.afterStart")}</Reason> : null}
        </div>
      ) : null}
      {canChange && mode === "held" ? (
        <form className="g-appointments__form" onSubmit={submitHeld} noValidate>
          <TextField id={`${ids}-held`} label={t("appointments.block.heldMinutes")} type="number" inputMode="numeric" min={0} max={480} value={heldMinutes} onChange={(event) => setHeldMinutes(event.target.value)} />
          <div className="g-appointments__actions">
            <Button variant="primary" type="submit" disabled={change.isPending}>
              {t("appointments.block.saveHeld")}
            </Button>
            <Button onClick={() => setMode("idle")}>{t("appointments.block.keep")}</Button>
          </div>
        </form>
      ) : null}
      {canChange && mode === "cancel" ? (
        <form className="g-appointments__form" onSubmit={submitCancel} noValidate>
          <TextField id={`${ids}-reason`} label={t("appointments.block.cancelReason")} value={reason} maxLength={500} onChange={(event) => setReason(event.target.value)} required />
          <div className="g-appointments__actions">
            <Button variant="primary" type="submit" disabled={change.isPending || reason.trim() === ""}>
              {t("appointments.block.cancelConfirm")}
            </Button>
            <Button onClick={() => setMode("idle")}>{t("appointments.block.keep")}</Button>
          </div>
        </form>
      ) : null}
      {mode === "move" ? <BookAppointmentDialog tab={tab} leadId={card.lead.id} timeZone={timeZone} moving={appointment} onClose={() => setMode("idle")} /> : null}
    </li>
  );
}
